export type FormatAmountOptionsType = {
  decimals?: number;
  decimalsSeparator?: string;
  digits?: number;
  digitsSeparator?: string;
  maxValue?: number;
  isAvailableFloat?: boolean;
  isAvailableNegative?: boolean;
  isSplit?: boolean;
  isSliced?: boolean;
}

export const AVAILABLE_INPUTED_SEPARATORS = ['.', ',']

export const DEFAULT_DECIMALS = 2
export const DEFAULT_DECIMALS_SEPARATOR = ','
export const DEFAULT_DIGITS = 3
export const DEFAULT_DIGITS_SEPARATOR = ' '
export const DEFAULT_MAX_VALUE = NaN
export const DEFAULT_IS_AVAILABLE_FLOAT = true
export const DEFAULT_IS_AVAILABLE_NEGATIVE = false
export const DEFAULT_IS_SPLIT = true
export const DEFAULT_IS_SLICED = true
export const DEFAULT_AMOUNT_FORMATTER_OPTIONS: FormatAmountOptionsType = {
  decimals: DEFAULT_DECIMALS,
  decimalsSeparator: DEFAULT_DECIMALS_SEPARATOR,
  digits: DEFAULT_DIGITS,
  digitsSeparator: DEFAULT_DIGITS_SEPARATOR,
  maxValue: DEFAULT_MAX_VALUE,
  isAvailableFloat: DEFAULT_IS_AVAILABLE_FLOAT,
  isAvailableNegative: DEFAULT_IS_AVAILABLE_NEGATIVE,
  isSplit: DEFAULT_IS_SPLIT,
  isSliced: DEFAULT_IS_SLICED
}
export const conformToAmountSymbols = (value: string | number, options: FormatAmountOptionsType = DEFAULT_AMOUNT_FORMATTER_OPTIONS) => {
  const allOptions: FormatAmountOptionsType = {
    ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
    ...options
  }

  let preparedValue = `${value}`
  const isNegativeValue = Array.from(preparedValue)[0] === '-'

  if (isNegativeValue) {
    preparedValue = preparedValue.slice(1)
  }

  if (!preparedValue || preparedValue === '-') {
    return isNegativeValue && allOptions.isAvailableNegative ? '-' : preparedValue
  }

  if (!allOptions.isAvailableFloat) {
    const onlyNumbersRule = /[^\d]/g
    const noStartZerosRule = /^[0]+/

    return preparedValue.replace(onlyNumbersRule, '').replace(noStartZerosRule, '')
  }

  const separator = allOptions.decimalsSeparator

  const firstRule = new RegExp(`[\\${AVAILABLE_INPUTED_SEPARATORS[0]}\\${AVAILABLE_INPUTED_SEPARATORS[1]}]`, 'g')
  const secondRule = new RegExp(`[^\\d\\${separator}]`, 'g')
  const thirdRule = new RegExp(`^\\${separator}`)
  const fourthRule = new RegExp(`\\${separator}`)
  const fifthRule = new RegExp(`\\${separator}`, 'g')
  const uniqSymbol = Symbol('').toString()

  let clearedValue = preparedValue
    // Приводим все доступные для ввода разделители дробных чисел к одному виду
    .replace(firstRule, separator!)
    // Удаляем всё кроме цифр и разделителя дробных чисел
    .replace(secondRule, '')
    // Удаляем разделитель, если он вначале строки
    .replace(thirdRule, '')

  // Если вначале строки ноль, а следом за ним любой символ, кроме разделителя, то добавляем разделитель после этого ноля
  if (clearedValue.length > 1 && Array.from(clearedValue)[0] === '0' && Array.from(clearedValue)[1] !== separator) {
    clearedValue = `0${separator}${clearedValue.slice(1)}`
  }

  const resultValue = clearedValue
    // Следующие три действия осуществляют запрет ввода более одного разделителя
    // Заменяем первый найденный разделитель на уникальный символ
    .replace(fourthRule, uniqSymbol)
    // Удаляем все оставшиеся разделители
    .replace(fifthRule, '')
    // Заменяем обратно уникальный символ на разделитель
    .replace(uniqSymbol, separator!)

  return isNegativeValue && allOptions.isAvailableNegative ? `-${resultValue}` : resultValue
}

export const splitIntegerByDigits = (value: string | number, options: FormatAmountOptionsType = DEFAULT_AMOUNT_FORMATTER_OPTIONS) => {
  const allOptions: FormatAmountOptionsType = {
    ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
    ...options
  }

  let preparedValue = `${value}`

  const isNegativeValue = Array.from(preparedValue)[0] === '-'

  if (isNegativeValue) {
    preparedValue = preparedValue.slice(1)
  }

  if (!preparedValue || preparedValue === '-') {
    return isNegativeValue && allOptions.isAvailableNegative ? '-' : preparedValue
  }

  const regExpRule = new RegExp(`.{1,${allOptions.digits}}`, 'g')

  // Переворачиваем строку '1234567' -> '7654321'
  const reversedValue = preparedValue.split('').reverse().join('')
  // Возвращаем список совпадений по условию регулярного выражения (выбор групп символов от 1 до n) '7654321' -> ['765', '432', '1']
  const splittedValue = reversedValue.match(regExpRule)
  // Переворачиваем каждый элемент в полученном списке ['765', '432', '1'] -> ['567', '234', '1']
  const mappedValue = splittedValue!.map(digitGroup => digitGroup.split('').reverse().join(''))
  // Переворачиваем массив и соединяем его в строку через разделитель разрядов ['567', '234', '1'] -> ['1', '234', '567'] -> '1 234 567'
  const resultValue = mappedValue.reverse().join(allOptions.digitsSeparator!).trim()

  return isNegativeValue && allOptions.isAvailableNegative ? `-${resultValue}` : resultValue
}

export const formatAmount = (value: string | number, options: FormatAmountOptionsType = DEFAULT_AMOUNT_FORMATTER_OPTIONS) => {
  const allOptions: FormatAmountOptionsType = {
    ...DEFAULT_AMOUNT_FORMATTER_OPTIONS,
    ...options
  }

  let preparedValue = `${value}`

  const isNegativeValue = Array.from(preparedValue)[0] === '-'

  if (isNegativeValue) {
    preparedValue = preparedValue.slice(1)
  }

  if (!preparedValue || preparedValue === '-') {
    return isNegativeValue && allOptions.isAvailableNegative ? '-' : preparedValue
  }

  let conformedValue = conformToAmountSymbols(preparedValue, allOptions)
  const recoveredValue = isNegativeValue ? `-${conformedValue}` : conformedValue

  if (!isNaN(options.maxValue!) && !isNaN(Number(recoveredValue)) && Number(recoveredValue) > options.maxValue!) {
    conformedValue = options.maxValue! >= 0 ? `${options.maxValue}` : `${options.maxValue}`.slice(1)
  }

  if (conformedValue.indexOf(allOptions.decimalsSeparator!) !== -1) {
    const splittedValue = conformedValue.split(allOptions.decimalsSeparator!)
    const integerPart = allOptions.isSplit
      ? splitIntegerByDigits(splittedValue[0], allOptions)
      : splittedValue[0]
    const fractionPart = allOptions.isSliced
      ? splittedValue[1]?.slice(0, allOptions.decimals!) ?? ''
      : splittedValue[1] || ''
    const concatinatedValue = `${integerPart}${allOptions.decimalsSeparator}${fractionPart}`

    return isNegativeValue && allOptions.isAvailableNegative ? `-${concatinatedValue}` : concatinatedValue
  }

  const resultValue = allOptions.isSplit
    ? splitIntegerByDigits(conformedValue, allOptions)
    : conformedValue
  return isNegativeValue && allOptions.isAvailableNegative ? `-${resultValue}` : resultValue
}

export const recoverAmount = (value: string | number, onlyInteger: boolean = false, decimalsSeparator: string = DEFAULT_DECIMALS_SEPARATOR) => {
  const preparedValue = onlyInteger ? `${value}`.split(decimalsSeparator)[0] : `${value}`
  return preparedValue.replace(/\s/g, '')
}

export const prepareAmountForUrlQuery = (value: string, onlyInteger: boolean = false, decimalsSeparator: string = DEFAULT_DECIMALS_SEPARATOR) => {
  return recoverAmount(value, onlyInteger, decimalsSeparator).replace(decimalsSeparator, '.')
}
